/**
 * FilterChips — affiche chaque filtre actif sous forme de chip avec ✕.
 *
 *   <FilterChips
 *     chips={[
 *       { key: 'q', label: 'Recherche : "bleu"' },
 *       { key: 'tag-cobalt', label: 'Tag : bleu cobalt' },
 *       { key: 'orientation', label: 'Format : portrait' },
 *     ]}
 *     onRemove={(key) => removeFilter(key)}
 *     onClearAll={clearAllFilters}
 *   />
 *
 * Le composant est purement presentationnel — la logique de mapping
 * "filtre URL → chips" vit dans la page parente (`useListFilters` en S2.4).
 */
import { X } from 'lucide-react'

import './FilterChips.css'

export interface FilterChip {
  /** Cle stable utilisee par `onRemove`. Doit etre unique dans la liste. */
  key: string
  /** Texte affiche dans la chip (ex: "Tag : bleu cobalt"). */
  label: string
}

interface FilterChipsProps {
  chips: FilterChip[]
  onRemove: (key: string) => void
  /** Si fourni, affiche un bouton "Effacer tout" en fin de ligne. */
  onClearAll?: () => void
  className?: string
}

export default function FilterChips({ chips, onRemove, onClearAll, className }: FilterChipsProps) {
  if (chips.length === 0) return null

  const classes = ['filter-chips', className].filter(Boolean).join(' ')

  return (
    <div className={classes} role="list" aria-label="Filtres actifs">
      {chips.map((chip) => (
        <span key={chip.key} className="filter-chips-chip" role="listitem">
          <span className="filter-chips-label">{chip.label}</span>
          <button
            type="button"
            className="filter-chips-remove"
            onClick={() => onRemove(chip.key)}
            aria-label={`Retirer le filtre ${chip.label}`}
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}
      {onClearAll && chips.length > 1 && (
        <button type="button" className="filter-chips-clear-all" onClick={onClearAll}>
          Effacer tout
        </button>
      )}
    </div>
  )
}
